// Thư mục: src/stores/adminXangDauStore.js
import { defineStore } from "pinia";
import {
  XangDauApi,
  XeOptionsApi,
  TaiXeOptionsApi,
} from "@/services/xangDauService"; // Import chung

export const useAdminXangDauStore = defineStore("adminXangDau", {
  state: () => ({
    // State cho danh sách
    isLoadingList: false,
    listError: null,
    items: [], // Danh sách XangDauLogResponse
    page: 0,
    size: 20,
    sort: "ngayDo,desc", // Sắp xếp theo ngày đổ
    totalPages: 1,
    totalElements: 0,

    // State cho Filters
    filters: {
      xeId: null,
      tuNgay: null,
      denNgay: null,
      trangThai: "CHO_DUYET", // Mặc định hiển thị tab "Chờ Duyệt"
    },

    // State cho Modal Chi tiết
    isLoadingDetail: false, // Loading khi mở modal hoặc thực hiện action
    detailError: null, // Lỗi xảy ra trong modal
    currentLogDetail: null, // Log đang xem (XangDauLogResponse)

    editHistory: [],
    isLoadingHistory: false,
    historyError: null,

    isSubmittingCorrection: false,
    submitCorrectionError: null,

    // State cho Options (dùng cho filter)
    isLoadingOptions: false,
    xeOptions: [], // [{id, bienSo}]
    taiXeOptions: [], // [{idNguoiDung, hoTen}]
  }),

  getters: {
    currentPage(state) {
      return state.page + 1;
    },
  },

  actions: {
    // --- Actions Phân trang & Filter ---
    setPage(p1) {
      this.page = Math.max(0, p1 - 1);
      this.fetchList(); // Tải lại trang mới
    },
    setSize(s) {
      this.size = s;
      this.page = 0;
      this.fetchList();
    },
    /**
     * Action này được gọi khi bấm nút Lọc hoặc bấm Tab
     */
    async applyFiltersAndFetch(newFilters) {
      this.filters = { ...this.filters, ...newFilters }; // Cập nhật filter
      this.page = 0; // Reset về trang đầu
      await this.fetchList(); // Tải lại
    },

    // --- Action Tải Danh sách Log (Chính) ---
    async fetchList() {
      this.isLoadingList = true;
      this.listError = null;
      try {
        const params = {
          page: this.page,
          size: this.size,
          sort: this.sort,
          // Gửi các filter state đi
          xeId: this.filters.xeId,
          tuNgay: this.filters.tuNgay,
          denNgay: this.filters.denNgay,
          trangThai: this.filters.trangThai, // (Backend sẽ xử lý 'CHO_DUYET', 'DA_DUYET', ...)
        };
        const data = await XangDauApi.listLogs(params);
        this.items = data.content || [];
        this.totalPages = data.totalPages ?? 1;
        this.totalElements = data.totalElements ?? this.items.length;
        this.page = data.number ?? 0;
      } catch (e) {
        this.listError =
          e?.response?.data?.message ||
          e?.message ||
          "Lỗi tải lịch sử xăng dầu";
        console.error("fetchList XangDau error:", e);
      } finally {
        this.isLoadingList = false;
      }
    },

    // --- Action Tải Chi tiết Log (cho Modal) ---
    async fetchLogDetail(logId) {
      this.isLoadingDetail = true;
      this.detailError = null;
      this.currentLogDetail = null;
      try {
        this.currentLogDetail = await XangDauApi.getById(logId);
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi tải chi tiết log";
        console.error("fetchLogDetail error:", e);
      } finally {
        this.isLoadingDetail = false;
      }
    },
    clearLogDetail() {
      this.currentLogDetail = null;
      this.detailError = null;
    },

    // --- Action Tải Options cho Filter ---
    async fetchOptionsIfNeeded() {
      if (this.xeOptions.length > 0) return; // Chỉ tải 1 lần
      this.isLoadingOptions = true;
      try {
        const [xeData, taiXeData] = await Promise.all([
          XeOptionsApi.listForSelect(),
          TaiXeOptionsApi.listForSelect(),
        ]);
        this.xeOptions = xeData || [];
        this.taiXeOptions = (taiXeData || []).map((nd) => ({
          id: nd.idNguoiDung,
          hoTen: nd.hoTen,
        }));
      } catch (e) {
        console.error("Lỗi tải options Xe/Tài xế:", e);
      } finally {
        this.isLoadingOptions = false;
      }
    },

    // --- Actions Duyệt/Từ chối ---
    async approveLog(logId) {
      this.isLoadingDetail = true; // Dùng loading của modal
      this.detailError = null;
      try {
        const updatedLog = await XangDauApi.approve(logId);
        this.currentLogDetail = updatedLog; // Cập nhật log hiện tại
        await this.fetchList(); // Tải lại danh sách
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi khi duyệt log";
        console.error("approveLog error:", e);
        throw e; // Ném lỗi ra modal
      } finally {
        this.isLoadingDetail = false;
      }
    },
    async rejectLog(logId, lyDo) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        const updatedLog = await XangDauApi.reject(logId, lyDo);
        this.currentLogDetail = updatedLog; // Cập nhật log hiện tại
        await this.fetchList(); // Tải lại danh sách
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi khi từ chối log";
        console.error("rejectLog error:", e);
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async correctLog(logId, payload) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        // GỌI API SỬA: PUT /api/xang-dau/{id}/correct
        // return await XangDauApi.correctLog(logId, payload);
        await XangDauApi.correctLog(logId, payload); // implement trong services
      } catch (e) {
        console.error(e);
        this.detailError = e?.response?.data?.message || "Không thể chỉnh sửa";
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async fetchEditHistory(logId) {
      this.isLoadingHistory = true;
      this.historyError = null;
      try {
        const list = await XangDauApi.getEditHistory(logId);
        // Kỳ vọng BE trả mảng item dạng:
        // { id, editedAt, editorId, editorName, lyDo, soTienTruoc, soTienSau, soLitTruoc, soLitSau,
        //   odoKmCuoiKyTruoc, odoKmCuoiKySau, ngayDoTruoc, ngayDoSau, ghiChuTruoc, ghiChuSau, anhUrls[] }
        this.editHistory = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error(e);
        this.historyError = "Không tải được lịch sử chỉnh sửa.";
      } finally {
        this.isLoadingHistory = false;
      }
    },
  },
});
